import dayjs, { Dayjs } from "dayjs";
import { startOfMondayWeek } from "../../Franchisee/TeacherActivity/activityPeriod.ts";
import { NewUsersOnDay } from "./useUserStatistics.ts";

export type NewUsersGranularity = "week" | "month";

export type NewUsersBucket = {
  start: Dayjs;
  label: string;
  userCount: number;
  isInProgress: boolean;
};

export const NEW_USERS_BUCKET_COUNT = 12;
export const NEW_USERS_COMPARISON_DAYS = 30;

const DAY_FORMAT = "YYYY-MM-DD";

function bucketStartOf(day: Dayjs, granularity: NewUsersGranularity): Dayjs {
  return granularity === "week" ? startOfMondayWeek(day) : day.startOf("month");
}

function bucketLabel(start: Dayjs, granularity: NewUsersGranularity): string {
  return start.format(granularity === "week" ? "D MMM" : "MMM YY");
}

export function newUsersQueryRange(today: Dayjs): {
  fromDay: string;
  toDay: string;
} {
  const firstMonthShown = bucketStartOf(today, "month").subtract(
    NEW_USERS_BUCKET_COUNT - 1,
    "month",
  );
  return {
    fromDay: firstMonthShown.format(DAY_FORMAT),
    toDay: today.format(DAY_FORMAT),
  };
}

export function newUsersBuckets({
  newUsersPerDay,
  granularity,
  today,
}: {
  newUsersPerDay: NewUsersOnDay[];
  granularity: NewUsersGranularity;
  today: Dayjs;
}): NewUsersBucket[] {
  const currentBucketStart = bucketStartOf(today, granularity);
  const bucketStarts = Array.from(
    { length: NEW_USERS_BUCKET_COUNT },
    (_, bucketIndex) =>
      currentBucketStart.subtract(
        NEW_USERS_BUCKET_COUNT - 1 - bucketIndex,
        granularity,
      ),
  );
  const userCountByBucketStart = new Map<number, number>();
  newUsersPerDay.forEach(({ day, userCount }) => {
    const bucketStart = bucketStartOf(dayjs(day, DAY_FORMAT), granularity);
    userCountByBucketStart.set(
      bucketStart.valueOf(),
      (userCountByBucketStart.get(bucketStart.valueOf()) ?? 0) + userCount,
    );
  });
  return bucketStarts.map((start) => ({
    start,
    label: bucketLabel(start, granularity),
    userCount: userCountByBucketStart.get(start.valueOf()) ?? 0,
    isInProgress: start.isSame(currentBucketStart),
  }));
}

export function newUsersInRecentDays({
  newUsersPerDay,
  today,
}: {
  newUsersPerDay: NewUsersOnDay[];
  today: Dayjs;
}): { lastDays: number; previousDays: number } {
  const lastDaysStart = today
    .startOf("day")
    .subtract(NEW_USERS_COMPARISON_DAYS - 1, "day");
  const previousDaysStart = lastDaysStart.subtract(
    NEW_USERS_COMPARISON_DAYS,
    "day",
  );
  return newUsersPerDay.reduce(
    (counts, { day, userCount }) => {
      const date = dayjs(day, DAY_FORMAT);
      if (!date.isBefore(lastDaysStart))
        return { ...counts, lastDays: counts.lastDays + userCount };
      if (!date.isBefore(previousDaysStart))
        return { ...counts, previousDays: counts.previousDays + userCount };
      return counts;
    },
    { lastDays: 0, previousDays: 0 },
  );
}
